import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useClientAuth } from '../../auth/ClientAuthContext';
import API_BASE from '../../utils/api';
import PublicStripePaymentModal from '../../components/Projects/Invoices/PublicStripePaymentModal';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function ClientRetainerDetail() {
  const { projectId, retainerId } = useParams();
  const { clientAuth } = useClientAuth();
  const [retainer, setRetainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (!clientAuth?.token) return;

    fetch(`${API_BASE}/api/client/projects/${projectId}/retainers/${retainerId}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${clientAuth.token}` },
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
        return data;
      })
      .then(setRetainer)
      .catch((err) => setError(err.message || 'Failed to load retainer.'))
      .finally(() => setLoading(false));
  }, [clientAuth, projectId, retainerId]);

  if (loading) return <p style={{ color: '#6b7280', fontSize: 14 }}>Loading…</p>;
  if (error) return <><Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link><p className="cp-error">{error}</p></>;
  if (!retainer) return null;

  const requested = Number(retainer.amount || 0);
  const funded = Number(retainer.payments || 0);
  const available = Number(retainer.availableBalance || 0);
  const applied = Number(retainer.appliedAmount || 0);
  const paymentHistory = Array.isArray(retainer.paymentHistory) ? retainer.paymentHistory : [];

  return (
    <div className="cp-animate">
      <Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link>

      <div className="cp-card">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
            {retainer.retainerNumber || 'Retainer'}
          </h1>
          {retainer.description && (
            <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>{retainer.description}</p>
          )}
          {retainer.requestDate && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              Requested: {new Date(retainer.requestDate).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="cp-totals" style={{ marginBottom: 28 }}>
          <div className="cp-totals-inner">
            {requested > 0 && <TotalRow label="Requested Amount" value={requested} />}
            <TotalRow label="Funded" value={funded} bold />
            {applied > 0 && <TotalRow label="Applied to Invoices" value={applied} />}
            <TotalRow label="Available Balance" value={available} />
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 22, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="cp-btn-primary" onClick={() => setShowPayment(true)}>
            Fund Retainer
          </button>
        </div>

        {paymentHistory.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Funding History</h2>
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Method</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((p) => (
                  <tr key={p.paymentId}>
                    <td>{p.date ? new Date(p.date).toLocaleDateString() : '-'}</td>
                    <td>{p.method || p.paymentType || '-'}</td>
                    <td className="right">{formatCurrency(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showPayment && (
        <PublicStripePaymentModal
          remainingBalance={requested > funded ? requested - funded : null}
          title="Fund Retainer"
          invoiceNumber={retainer.retainerNumber}
          createIntentEndpoint={`${API_BASE}/api/client/projects/${projectId}/retainers/${retainerId}/create-payment-intent`}
          applyPaymentEndpoint={`${API_BASE}/api/client/projects/${projectId}/retainers/${retainerId}/apply-payment`}
          headers={{ Authorization: `Bearer ${clientAuth.token}` }}
          metadata={{ retainerId, projectId }}
          onSuccess={(amount) => {
            setRetainer((prev) => ({
              ...prev,
              payments: Number((Number(prev.payments || 0) + amount).toFixed(2)),
              availableBalance: Number((Number(prev.availableBalance || 0) + amount).toFixed(2)),
            }));
            setShowPayment(false);
          }}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div className={`cp-total-row${bold ? ' bold' : ''}`}>
      <span>{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}
